import React, { FC, useEffect } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { ReportClaimIcon, RequestQuoteDocumentIcon } from "@/assets/icons";
import { ActivitiesPanel } from "@/components/features/Activities";
import { StandardLayout } from "@/components/layouts";
import { DetailsPage } from "@/components/templates";
import { StyledActionItem } from "@/components/templates/DetailsPage/styled";
import mobileIntegration from "@/integration";
import { insuredGoodApi } from "@/shared/apis";
import { InsuredGood, InsuredGoodDetailPageState, Permissions } from "@/shared/domain";
import { isABroker, useAuth, useBreadcrumb, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import { RelatedClaims } from "../../Claims";
import InsuredGoodItem from "../components/InsuredGoodItem/InsuredGoodItem";
import RelatedContracts from "./components/RelatedContracts/RelatedContracts";
import GoodsTabPanel from "./components/TabPanels/GoodsTabPanel/GoodsTabPanel";
import ObjVarTabPanel from "./components/TabPanels/ObjVarTabPanel/ObjVarTabPanel";
import ShipTabPanel from "./components/TabPanels/ShipTabPanel/ShipTabPanel";
import TrailerTabPanel from "./components/TabPanels/TrailerTabPanel/TrailerTabPanel";
import VehicleTabPanel from "./components/TabPanels/VehicleTabPanel/VehicleTabPanel";

const InsuredGoodDetailPage: FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isNativeApp = mobileIntegration.isNativeApp();
    const { setPageTitle } = useBreadcrumb();
    const { getStateOrSearch } = useRouterHook<InsuredGoodDetailPageState>();
    const {
        navToRelatedDocuments,
        navToContextualConversation,
        navToInsuredGoods,
        navToRelatedActivities,
        navToReportClaimPageFor,
        navToTarifficationDetails,
    } = useNavigation();
    const { incUsageCount } = useUserFeedback();

    const { id: insuredGoodId } = useParams<{ id: string }>();

    const insuredGoodType = getStateOrSearch("insuredGoodType");
    const canRequestGreenCardState = getStateOrSearch("canRequestGreenCard");
    const canRequestGreenCard =
        (typeof canRequestGreenCardState !== "string" && Boolean(canRequestGreenCardState)) ||
        canRequestGreenCardState === "true";

    const policyholderId = getStateOrSearch("policyholderId");

    const areVariablesEmpties = insuredGoodType == null || policyholderId == null || canRequestGreenCard == null;
    const ibpAclId = user.getIbpAclId(policyholderId);

    const {
        data: insuredGood,
        isLoading,
        isFetched,
    } = useQuery(
        ["insuredGood", { insuredGoodId }],
        () =>
            insuredGoodApi.getInsuredGoodDetails(
                +insuredGoodId,
                insuredGoodType,
                +policyholderId,
                ibpAclId,
                canRequestGreenCard,
            ),
        {
            enabled: !areVariablesEmpties,
        },
    );

    useEffect(() => {
        if (!isFetched && areVariablesEmpties) {
            navToInsuredGoods("0");
        }

        if (insuredGood) {
            setPageTitle(insuredGood.title);
        }
    }, [isFetched]);

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemEvent("insuredGood", "Insured Goods");
    });

    const { details = {} } = insuredGood || {};
    const insuredGoodKey = Object.keys(details)[0];

    const IGPolicyholderId = +policyholderId || insuredGood?.policyHolderId;

    const allowedToSeeActivities = user.hasPermission(Permissions.ViewInsuredGoodActivities, IGPolicyholderId);

    const detailsTabPanel = () => {
        switch (insuredGoodKey) {
            case "vehicle":
                return <VehicleTabPanel details={details.vehicle} />;
            case "trailer":
                return <TrailerTabPanel details={details.trailer} />;
            case "ship":
                return <ShipTabPanel details={details.ship} />;
            case "building":
                return <GoodsTabPanel details={details.building} />;
            case "objVar":
            default:
                return <ObjVarTabPanel details={details.objVar} />;
        }
    };

    const getInsuredGood = (): InsuredGood => {
        return {
            ...insuredGood,
            name: insuredGood.title,
            canRequestGreenCard: details?.vehicle?.canRequestGreenCard || false,
            id: Number(insuredGoodId),
            policyHolder: insuredGood.policyHolderName,
            policyholderId: insuredGood.policyHolderId,
        };
    };

    const handleShowMoreActivities = () => {
        navToRelatedActivities(insuredGoodId, insuredGood.title, "insuredgood", IGPolicyholderId, ibpAclId);
    };

    const handleShowDocuments = () => {
        navToRelatedDocuments(insuredGoodId, insuredGood.title, "insuredGoods");
    };

    const handleAccessConversations = () => {
        navToContextualConversation(insuredGoodId, "risk", insuredGood.title, policyholderId);
    };

    const handleShowDeclareClaim = () => {
        navToReportClaimPageFor(insuredGoodId, IGPolicyholderId);
    };

    const extraButtons = () => {
        const buttons = [];
        if (!isNativeApp) {
            if (user.hasPermission(Permissions.AccessDeclareClaim, IGPolicyholderId)) {
                buttons.push(
                    <span
                        key="actionItem.ReportClaim"
                        title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}
                    >
                        <StyledActionItem
                            className={clsx(isABroker() && "disabled")}
                            onClick={handleShowDeclareClaim}
                            $disabled={isABroker()}
                            startIcon={<ReportClaimIcon />}
                            data-testid="insured_good_detail_page-button-declare-claim"
                        >
                            {t("InsuredGood.ReportClaim")}
                        </StyledActionItem>
                    </span>,
                );
            }
            if (
                insuredGoodKey === "vehicle" &&
                user.hasPermission(Permissions.AccessTariffication, insuredGood.policyHolderId)
            ) {
                buttons.push(
                    <span
                        key="actionItem.RequestTariffication"
                        title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}
                    >
                        <StyledActionItem
                            className={clsx(isABroker() && "disabled")}
                            onClick={() => navToTarifficationDetails(IGPolicyholderId, insuredGoodId)}
                            $disabled={isABroker()}
                            startIcon={<RequestQuoteDocumentIcon />}
                            data-testid="insured_good_detail_page-button-request-tariffication"
                        >
                            {t("Common.RequestAQuote")}
                        </StyledActionItem>
                    </span>,
                );
            }
        }
        return buttons;
    };

    const panels = [
        { label: t("Common.Details"), content: !isLoading && detailsTabPanel() },
        {
            label: t("InsuredGood.Contracts"),
            content: (
                <RelatedContracts
                    insuredGoodId={insuredGoodId}
                    featureType="insuredgood"
                    ibpAclId={ibpAclId}
                    policyholderId={IGPolicyholderId}
                />
            ),
        },
        {
            label: t("InsuredGood.Claims"),
            content: (
                <RelatedClaims
                    id={insuredGoodId}
                    featureType="insuredgood"
                    ibpAclId={ibpAclId}
                    policyholderId={IGPolicyholderId}
                />
            ),
        },
    ];

    return (
        <StandardLayout title={t("PageTitles.InsuredGoodsDetails")}>
            <DetailsPage
                panels={panels}
                extraButtons={extraButtons()}
                defaultSelectedTab={details[insuredGoodKey]?.linkedContractCount ? 1 : 0}
                objectType="insuredgood"
                handleShowDocuments={
                    user.hasPermission(Permissions.ViewLinkedDocuments, IGPolicyholderId) && handleShowDocuments
                }
                handleShowConversation={
                    user.hasPermission(Permissions.AccessConversations, IGPolicyholderId) && handleAccessConversations
                }
                activities={
                    allowedToSeeActivities && (
                        <ActivitiesPanel
                            policyholderId={IGPolicyholderId}
                            featureType="insuredgood"
                            onShowMoreClick={handleShowMoreActivities}
                        />
                    )
                }
                card={!isLoading && insuredGood && <InsuredGoodItem insuredGood={getInsuredGood()} />}
            />
        </StandardLayout>
    );
};

export default InsuredGoodDetailPage;
